import React from 'react';
import { ButtonGroup, Tag, TagGroup, TreeNavigation, TreeNavigationItem } from '@emorg-prd/standard-react';
import { Table, Grid, Fieldset, ProgressBar, AlertVariantType, Alert, Button } from '../common';
import {
	AbstractPolicyExemptionRequestForm,
	AbstractPolicyExemptionRequestFormState,
	AbstractPolicyExemptionRequestFormProps,
} from './AbstractPolicyExemptionRequestForm';
import PolicyExemptionRequest from '../../models/entities/PolicyExemptionRequest';
import AlertModal from '../common/modals/AlertModal';
import ConfirmModal from '../common/modals/ConfirmModal';
import { AlertBanner } from '../../models/interfaces/AlertBanner';
import Paginator from '../common/tables/Paginator';
import PolicyExemptionDefinition from '../../models/entities/PolicyExemptionDefinition';
import ReadOnlyField from '../common/form-controls/ReadOnly';
import WorkflowDetailWrapper from '../common/wrappers/WorkflowDetailWrapper';
import { AzureResourceInput, ProgressBarBig } from './components';
import SelectField from '../common/form-controls/SelectField';
import { useParams } from 'react-router-dom';
import RiskBadge from './components/RiskBadge';

interface PolicyExemptionRequestDetailProps extends AbstractPolicyExemptionRequestFormProps {}

interface PolicyExemptionRequestDetailState extends AbstractPolicyExemptionRequestFormState {
	loading: boolean;
	policyExemptionRequest?: PolicyExemptionRequest;
	embedAlert: AlertBanner;
	modalComment: string;
	readOnly: boolean;
	operationInProgress: boolean;
	inProgressMessage: string;
	decisionModalVisible: { approve: boolean; reject: boolean };
}

class PolicyExemptionRequestsDetailWithParams extends AbstractPolicyExemptionRequestForm<
	PolicyExemptionRequestDetailProps,
	PolicyExemptionRequestDetailState
> {
	constructor(props) {
		super(props);

		this.state = {
			stateForm: {
				displayName: '',
				businessJustification: '',
				riskAssessmentLink: '',
				durationRequested: '',
				policyDefinitions: [],
				policyAssignment: null,
				resource: null,
				resourceGroup: null,
			},
			subscription: null,
			loading: true,
			submissionWarned: false,
			endModal: {
				visible: false,
			},
			embedAlert: {
				visible: false,
			},
			readOnly: true,
			modalComment: '',
			operationInProgress: false,
			inProgressMessage: '',
			decisionModalVisible: { approve: false, reject: false },
		};

		super.initFormModel();
	}

	async componentWillMount() {
		const id = parseInt(this.props.params.id);
		const perid = parseInt(this.props.params.perid);
		await this.loadRequest(perid, id);
	}

	loadRequest = async (perid, id) => {
		this.setState(
			{
				loading: true,
			},
			async () => {
				try {
					const subscription = await this.getSubscription(id);

					const policyExemptionRequest = new PolicyExemptionRequest(
						await this.policyExemptionRequestService.getById(perid)
					);

					this.setState(
						{
							subscription,
							policyExemptionRequest,
							loading: false,
						},
						this.resetStateForm
					);
				} catch (error) {
					const HTTP_FORBIDDEN_ERROR_CODE = 403;
					if (error.status === HTTP_FORBIDDEN_ERROR_CODE) {
						this.handleEndModal(
							'You are not allowed to see this request.',
							'error',
							5000,
							`/subscriptions/${id}/policyExemptionRequests`
						);
					} else {
						this.handleEndModal(
							'The request could not be retrieved.',
							'error',
							10000,
							`/subscriptions/${id}/policyExemptionRequests`
						);
					}
				}
			}
		);
	};

	resetStateForm = () => {
		this.setState((prevState) => ({
			stateForm: this.stateFormFromRequest(prevState.policyExemptionRequest),
		}));
	};

	handleEndModal = (text: string, variant: AlertVariantType, timeout: number, link: string) => {
		this.setState(
			{
				endModal: {
					visible: true,
					text,
					variant,
					redirectionLink: link,
				},
			},
			() => {
				setTimeout(() => {
					this.endAndRedirect();
				}, timeout);
			}
		);
	};

	handleOnPageChange = (definitions: PolicyExemptionDefinition[]) => {
		const newReq = {
			...this.state.policyExemptionRequest,
			policyExemptionDefinitions: definitions,
		};

		this.setState({
			policyExemptionRequest: new PolicyExemptionRequest(newReq),
		});
	};

	needsApproval = () => {
		return true;
	};

	decisionModalConfirmationField = () => ({
		label: 'Please provide the reason.',
		value: this.state.modalComment,
		validation: { required: true },
		onChange: (ev) => this.setState({ modalComment: ev.target.value }),
	});

	openDecisionModal = (key: 'approve' | 'reject') => {
		this.setState({ decisionModalVisible: { approve: false, reject: false, [key]: true } });
	};

	closeDecisionModal = () => {
		this.setState({ decisionModalVisible: { approve: false, reject: false } });
	};

	handleOperationConfirmed = async ({
		operation,
		inProgressMessage,
		successMessage,
	}: {
		operation: () => Promise<any>;
		inProgressMessage: string;
		successMessage: string;
	}) => {
		this.setState({ operationInProgress: true, inProgressMessage });
		this.closeDecisionModal();
		try {
			await operation();
			await this.loadRequest(this.state.policyExemptionRequest?.id, this.state.subscription?.id);
			this.setState({ embedAlert: { visible: true, variant: 'success', text: successMessage } });
		} catch (err) {
			this.setState({
				embedAlert: {
					visible: true,
					variant: 'error',
					text: err?.response?.data?.message ?? 'An error occurred. Please try again later.',
				},
			});
		} finally {
			this.setState({ operationInProgress: false, inProgressMessage: '' });
		}
	};

	handleApprove = async () => {
		this.handleOperationConfirmed({
			operation: async () => {
				await this.policyExemptionRequestService.approve(this.state.policyExemptionRequest?.id, {
					reason: this.state.modalComment,
				});
			},
			inProgressMessage: 'Approving...',
			successMessage: 'Request approved successfully.',
		});
	};

	handleReject = async () => {
		this.handleOperationConfirmed({
			operation: async () => {
				await this.policyExemptionRequestService.reject(this.state.policyExemptionRequest?.id, {
					reason: this.state.modalComment,
				});
			},
			inProgressMessage: 'Rejecting...',
			successMessage: 'Request rejected successfully.',
		});
	};

	actions = () =>
		this.state.policyExemptionRequest?.canApprove() && (
			<ButtonGroup>
				<Button
					variant="primary"
					size="small"
					disabled={this.state.operationInProgress}
					isLoading={this.state.operationInProgress}
					onClick={() => this.openDecisionModal('approve')}
				>
					<span>Approve</span>
				</Button>
				<Button
					variant="secondary"
					size="small"
					disabled={this.state.operationInProgress}
					onClick={() => this.openDecisionModal('reject')}
				>
					<span>Reject</span>
				</Button>
			</ButtonGroup>
		);

	render() {
		const rows = this.state.policyExemptionRequest?.policyExemptionDefinitions.map((definition) => (
			<Table.Row key={definition.id}>
				<Table.Cell>{definition.referenceId ?? '-'}</Table.Cell>
				<Table.Cell>{definition.displayName}</Table.Cell>
				<Table.Cell>
					<RiskBadge risk={definition.riskRating} />
				</Table.Cell>
			</Table.Row>
		));

		const exemptions =
			this.state.policyExemptionRequest?.policyExemptionAssignments.length > 1
				? this.state.policyExemptionRequest?.policyExemptionAssignments.map((assignment) => (
						<Table.Row key={assignment.id}>
							<Table.Cell>
								{`${this.state.policyExemptionRequest?.azureName}-${assignment.scopeDisplayName}`}
							</Table.Cell>
							<Table.Cell>{assignment.scopeDisplayName}</Table.Cell>
						</Table.Row>
					))
				: null;

		return (
			<>
				<WorkflowDetailWrapper
					title={`Policy Exception Request #${this.state.policyExemptionRequest?.id}`}
					linkTree={this.getLinkTree()}
					workflowInstance={this.state.policyExemptionRequest?.workflowInstance}
					loading={this.state.loading}
					actions={this.actions()}
					showHistoryButton
					fromPOR
				>
					{this.state.loading ? (
						<ProgressBar
							className="em-u-margin-top-half"
							indeterminate
							label="Loading Policy Exception Request..."
						/>
					) : (
						<>
							{this.state.embedAlert.visible && (
								<Alert
									variant={this.state.embedAlert.variant as any}
									onClose={() => this.setState({ embedAlert: { visible: false } })}
									body={this.state.embedAlert.text}
								/>
							)}
							{this.state.operationInProgress && (
								<Alert variant="default" body={this.state.inProgressMessage} />
							)}
							<Grid variant="halves">
								<Grid.Item>
									<ReadOnlyField
										label="Exception Name"
										text={this.stateFormHandler().displayName.value}
										note={
											exemptions === null
												? `Azure Name: ${this.state.policyExemptionRequest.azureName}`
												: '(The exception was implemented for multiple scope levels)'
										}
									/>
								</Grid.Item>
								<Grid.Item>
									{exemptions === null && (
										<>
											<strong>Scope</strong>
											<br />
											<TagGroup color="em-c-tags__theme-blue">
												<Tag
													label={
														this.state.policyExemptionRequest.policyExemptionAssignments[0]
															.scopeDisplayName
													}
												/>
											</TagGroup>
										</>
									)}
								</Grid.Item>
							</Grid>
							{exemptions != null && (
								<Fieldset legend="Azure Policy Exceptions">
									<Table>
										<Table.Head>
											<Table.Head.Row>
												<Table.Head.Cell> AZURE NAME </Table.Head.Cell>
												<Table.Head.Cell> SCOPE </Table.Head.Cell>
											</Table.Head.Row>
										</Table.Head>
										<Table.Body>{exemptions}</Table.Body>
									</Table>
								</Fieldset>
							)}
							<Grid variant="halves">
								<Grid.Item>
									<SelectField
										label="Duration Requested"
										value={this.stateFormHandler().durationRequested.value}
										disabled={this.state.readOnly}
										options={this.stateFormHandler().durationRequested.options}
									/>
								</Grid.Item>
								<Grid.Item>
									<ReadOnlyField
										label="Approval Type"
										text={this.state.policyExemptionRequest?.approvalType?.name}
									/>
								</Grid.Item>
							</Grid>
							<Grid>
								<Grid.Item>
									<ProgressBarBig
										workflowState={
											this.state.policyExemptionRequest?.workflowInstance
												.currentWorkflowInstanceState.workflowState
										}
										policyExemptionRequest={this.state.policyExemptionRequest}
									/>
								</Grid.Item>
							</Grid>
							<AzureResourceInput
								resourceGroup={this.stateFormHandler().resourceGroup}
								resource={this.stateFormHandler().resource}
								subscriptionId={this.state.subscription.id}
								disabled={this.state.readOnly}
							/>
							<ReadOnlyField
								label="Risk Assessment Link"
								text={this.stateFormHandler().riskAssessmentLink.value}
							/>
							<ReadOnlyField
								label="Business Justification"
								text={this.stateFormHandler().businessJustification.value}
							/>
							<ReadOnlyField
								label="Assignment Name"
								text={this.state.policyExemptionRequest.assignmentDisplayName}
							/>
							<Fieldset legend="Policy Definitions">
								<Table
									footer={
										<Paginator
											data={this.state.policyExemptionRequest.policyExemptionDefinitions}
											onPageChange={this.handleOnPageChange}
										/>
									}
								>
									<Table.Head>
										<Table.Head.Row>
											<Table.Head.Cell> REFERENCE ID </Table.Head.Cell>
											<Table.Head.Cell> NAME </Table.Head.Cell>
											<Table.Head.Cell> CATEGORY </Table.Head.Cell>
										</Table.Head.Row>
									</Table.Head>
									<Table.Body>{rows}</Table.Body>
								</Table>
							</Fieldset>
						</>
					)}
				</WorkflowDetailWrapper>
				<AlertModal {...this.state.endModal} willTimeout={false} onClose={this.endAndRedirect} />
				<ConfirmModal
					visible={this.state.decisionModalVisible.approve}
					title={`Approve Policy Exception Request #${this.state.policyExemptionRequest?.id}`}
					question="Are you sure you want to approve this request?"
					confirmButton={{ label: 'Approve', props: { variant: 'primary' } }}
					confirmationField={this.decisionModalConfirmationField()}
					onConfirm={this.handleApprove}
					onCancel={this.closeDecisionModal}
				/>
				<ConfirmModal
					visible={this.state.decisionModalVisible.reject}
					title={`Reject Policy Exception Request #${this.state.policyExemptionRequest?.id}`}
					question="Are you sure you want to reject this request?"
					confirmButton={{ label: 'Reject', props: { variant: 'primary' } }}
					confirmationField={this.decisionModalConfirmationField()}
					onConfirm={this.handleReject}
					onCancel={this.closeDecisionModal}
				/>
			</>
		);
	}
}

const PolicyExemptionRequestsDetail = () => {
	return <PolicyExemptionRequestsDetailWithParams params={useParams()} />;
};

export default PolicyExemptionRequestsDetail;
